<template>
  <div class="p-field" :class="{ 'p-float-label': labelStyle === 'float' }">
    <div :class="{ 'p-field-horizontal': labelStyle === 'inline' }">
      <label v-if="label" :for="id" class="label-text">
        {{ label }}
        <span v-if="required" class="text-red-500">*</span>
      </label>

      <!-- Input Wrapper -->
      <div :class="{ 'p-input-wrapper relative': hasPrefix || hasSuffix }">
        <div class="p-input-container">
          <div v-if="hasPrefix"
            class="absolute left-3 top-1/2 -translate-y-1/2 flex items-center z-10 w-5 justify-center">
            <slot name="prefix"></slot>
          </div>
          <div v-if="hasSuffix" class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
            <slot name="suffix"></slot>
          </div>
          <InputNumber ref="inputRef" :id="id" v-model="localValue" :placeholder="placeholder" :disabled="disabled"
            :readonly="readonly" :required="required" :min="min" :max="max" :step="step" :prefix="prefix"
            :suffix="suffix" :minFractionDigits="computedFractionDigits.minFractionDigits"
            :maxFractionDigits="computedFractionDigits.maxFractionDigits" :mode="mode"
            :locale="locale" :currency="effectiveCurrency" :currencyDisplay="currencyDisplay" :useGrouping="useGrouping"
            :showButtons="showButtons" :buttonLayout="buttonLayout" :decrementButtonClass="decrementButtonClass"
            :incrementButtonClass="incrementButtonClass" :incrementButtonIcon="incrementButtonIcon" :maxLength="maxLength"
            :decrementButtonIcon="decrementButtonIcon" :class="[
              'one-inputnumber w-full',
              {
                'p-invalid': isInvalid,
                // 'p-inputtext-sm': size === 'small',
                // 'p-inputtext-lg': size === 'large',
                'p-disabled': disabled,
                'pl-9': hasPrefix,
                'pr-9': hasSuffix
              }
            ]" :size="size" :aria-label="props['aria-label']" :aria-labelledby="props['aria-labelledby']"
            :aria-invalid="isInvalid" :aria-required="required" :aria-describedby="ariaDescribedBy" :pt="{
              root: { class: wrapperClass }
            }" v-bind="$attrs" @input="onInput" @change="onChange" @focus="onFocus" @blur="onBlur" @keydown="onKeydown" />
        </div>
      </div>
    </div>

    <!-- Error Messages -->
    <div v-if="showErrorMessages" class="p-error-messages">
      <small v-if="helperText" :id="helperTextId" class="p-error block">{{ helperText }}</small>
      <small v-for="(error, index) in localizedErrors" :key="index" :id="`${id}-error-${index}`" class="p-error block">
        {{ error }}
      </small>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import InputNumber from 'primevue/inputnumber'

export default defineComponent({
  name: 'ONEInputNumber',
  components: {
    InputNumber
  },
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'
import type { ONEInputNumberProps } from './types'

const props = withDefaults(defineProps<ONEInputNumberProps>(), {
  modelValue: null,
  min: undefined,
  max: undefined,
  step: 1,
  prefix: undefined,
  suffix: undefined,
  minFractionDigits: undefined,
  maxFractionDigits: undefined,
  mode: 'decimal',
  locale: 'en-IN',
  currency: undefined,
  currencyDisplay: undefined,
  useGrouping: true,
  maxLength:undefined,
  showButtons: false,
  buttonLayout: 'stacked',
  decrementButtonClass: undefined,
  incrementButtonClass: undefined,
  incrementButtonIcon: 'pi pi-plus',
  decrementButtonIcon: 'pi pi-minus',
  label: undefined,
  labelStyle: 'fixed',
  helperText: '',
  invalid: false,
  id: () => `one-inputnumber-${Math.random().toString(36).substr(2, 9)}`,
  placeholder: undefined,
  disabled: false,
  readonly: false,
  required: false,
  size: undefined,
  'aria-label': '',
  'aria-labelledby': '',
  validationRules: () => [],
  validateOnBlur: true,
  validateOnInput: false,
  validateOnChange: true,
  showErrors: true,
  name: undefined,
  formContext: true,
  wrapperClass: undefined,
  // Add slot props defaults
  hasPrefix: false,
  hasSuffix: false
})

const emit = defineEmits([
  'update:modelValue',
  'input',
  'change',
  'focus',
  'blur',
  'validation-error',
  'validation-success',
  'keydown'
])

// Form Context
const { formState } = useFormContext()
const { t } = useLocalization()

// Local State
const inputRef = ref<HTMLInputElement | null>(null)
const localValidationErrors = ref<string[]>([])
const isDirty = ref(false)
const isValidating = ref(false)

const localValue = computed({
  get: () => props.modelValue,
  set: (newValue) => {
    // emit('update:modelValue', newValue)
    if (formState && props.name) {
      formState.setFieldValue(props.name, newValue)
    }
  }
})

const currentErrors = computed(() => {
  if (formState && props.name) {
    return formState.errors.value[props.name] || []
  }
  return localValidationErrors.value
})

const localizedErrors = computed(() => {
  return currentErrors.value.map(error => {
    if (error.startsWith('validation.')) {
      return t(error)
    }
    if (error.toLowerCase().includes('required')) {
      return t('validation.required')
    }
    return error
  })
})

const isInvalid = computed(() => {
  return props.invalid || (currentErrors.value.length > 0 && isDirty.value)
})

const helperTextId = computed(() => `${props.id}-helper`)

const ariaDescribedBy = computed(() => {
  const ids = []
  if (props.helperText) ids.push(helperTextId.value)
  currentErrors.value.forEach((_, index) => ids.push(`${props.id}-error-${index}`))
  return ids.join(' ')
})

const showErrorMessages = computed(() => {
  return props.showErrors && (!!props.helperText || (currentErrors.value.length > 0 && isDirty.value))
})

const computedFractionDigits = computed(() => {
  if (props.mode === 'currency') {
    return {
      minFractionDigits: 0,
      maxFractionDigits: 0
    }
  }
  return {
    minFractionDigits: props.minFractionDigits,
    maxFractionDigits: props.maxFractionDigits
  }
})

// Ensure currency is provided when mode is currency
const effectiveCurrency = computed(() => {
  if (props.mode === 'currency' && !props.currency) {
    return 'INR' // Default to INR if no currency is specified
  }
  return props.currency
})

const validate = async (): Promise<boolean> => {
  if (formState && props.name) {
    return formState.validateField(props.name)
  }

  isValidating.value = true
  try {
    const errors: string[] = []
    const value = localValue.value

    for (const rule of props.validationRules) {
      let isValid = true
      const message = rule.message

      if (rule.validator) {
        isValid = await rule.validator(value)
      }

      if (!isValid && message) {
        errors.push(message)
      }
    }

    localValidationErrors.value = errors
    if (errors.length > 0) {
      emit('validation-error', errors)
      return false
    }

    emit('validation-success')
    return true
  } finally {
    isValidating.value = false
  }
}

const onKeydown = (event: Event) => {
  const keyEvent = event as KeyboardEvent

  // Always prevent Enter key from triggering form submission
  if (keyEvent.key === 'Enter') {
    event.preventDefault()
    event.stopPropagation()
    return
  }

  emit('keydown', event)
}

const onInput = async (event: any) => {
  emit('input', event.value)
  emit('update:modelValue', event.value) // Immediately update model value during typing
  isDirty.value = true
  if (props.validateOnInput) {
    await validate()
  }
}

const onFocus = (event: any) => {
  emit('focus', event.originalEvent)
}

const onBlur = async (event: any) => {
  emit('blur', event.originalEvent)
  isDirty.value = true
  if (formState && props.name) {
    formState.setFieldTouched(props.name, true)
  }
  if (props.validateOnBlur) {
    await validate()
  }
}

const onChange = async (event: any) => {
  emit('change', event.originalEvent)
  if (props.validateOnChange) {
    await validate()
  }
}

// Initialize form context
onMounted(() => {
  if (formState && props.name && props.formContext && props.validationRules.length > 0) {
    formState.addValidation(props.name, props.validationRules)
  }
})

// Watch for external validation triggers
watch(() => props.invalid, (newValue) => {
  if (newValue) {
    isDirty.value = true
  }
})

defineExpose({
  focus: () => inputRef.value?.focus(),
  blur: () => inputRef.value?.blur(),
  validate,
  validationErrors: currentErrors,
  isDirty,
  isValidating,
  isValid: computed(() => currentErrors.value.length === 0)
})
</script>

<style scoped>
.p-input-wrapper :deep(.p-inputtext) {
  padding-left: 2.5rem;
}

.p-input-wrapper :deep(.pi) {
  font-size: 1rem;
  line-height: 1;
}
</style>
